import DateTimeDisplay from '@/components/DateTime';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import {
  useGetOrganizationDomesticContentByIdQuery,
  useGetOrganizationPricingHoldbackByIdQuery,
} from '@/services/pricing';
import { useGetQuotesQuery } from '@/services/quotes';
import { Box, Card, Icon } from '@palmetto/palmetto-components';
import { useParams } from 'react-router-dom';

import { DomesticContentSetting, Quote, QuoteStatus } from 'types';

import { SubtitleBox } from '../Account/AccountFundingSummary.utils';

const ValueRow = ({ label, value, isLoading }: { label: string; value: number | undefined; isLoading: boolean }) => (
  <Box borderWidth="0 0 xs 0" borderColor="separator" padding="md 0" justifyContent="space-between" direction="row">
    <Box>{label}</Box>
    <Box fontWeight="bold">{isLoading || value === undefined ? '$ --' : currencyFormatter.format(value)}</Box>
  </Box>
);

export const PricingSettingsDetails = ({ account }: { account: any }) => {
  const { id } = useParams<{ id: string }>();
  const { data: quotes, isLoading: quotesIsLoading } = useGetQuotesQuery(
    { id: id ?? account?.id, programType: account?.programType },
    { skip: !account?.programType },
  );
  const activeQuote = quotes?.find((quote: Quote) => quote.status === QuoteStatus.active);

  const { data: standardHoldbackSettings, isLoading: standardHoldbackSettingsIsLoading } =
    useGetOrganizationPricingHoldbackByIdQuery(
      { alias: account?.licensedOrganizationId || '', holdbackId: activeQuote?.orgHoldbackId || '' },
      { skip: !account?.licensedOrganizationId || !activeQuote?.orgHoldbackId },
    );
  const { data: domesticContentModifierSettings, isLoading: domesticContentModifierSettingsIsLoading } =
    useGetOrganizationDomesticContentByIdQuery(
      {
        alias: account?.licensedOrganizationId || '',
        domesticContentModifierId: activeQuote?.orgDomesticContentModifierId || '',
      },
      { skip: !account?.licensedOrganizationId || !activeQuote?.orgDomesticContentModifierId },
    );
  const domesticContentAttestmentType = account?.systemDesign?.domesticContentAttestment?.attestmentType;
  const domesticContentModifiers = domesticContentModifierSettings?.domesticContentModifier?.values?.find(
    (modifier: DomesticContentSetting) => modifier.systemType === domesticContentAttestmentType,
  );

  const standardHoldbackValue = standardHoldbackSettings?.holdback?.amount;
  const domesticContentModifierValue = domesticContentModifiers?.modifier?.value;
  const domesticContentHoldbackValue = domesticContentModifiers?.holdback?.value;

  const isLoading = quotesIsLoading || standardHoldbackSettingsIsLoading || domesticContentModifierSettingsIsLoading;

  return (
    <>
      <Card padding="sm">
        <Card.Header>
          <SubtitleBox>PRICING SETTINGS</SubtitleBox>
        </Card.Header>
        <Box childGap="sm" width="100" padding="0 lg md lg" borderWidth="xs 0 0 0" borderColor="separator">
          <Box padding="xs 0 0 0">
            <ValueRow label="Standard Holdback Value ($/W)" value={standardHoldbackValue} isLoading={isLoading} />
            <ValueRow
              label="Domestic Content Modifier Value ($/W)"
              value={domesticContentModifierValue}
              isLoading={isLoading}
            />
            <ValueRow
              label="Domestic Content Holdback Value ($/W)"
              value={domesticContentHoldbackValue}
              isLoading={isLoading}
            />
            <Box padding="md 0 sm 0" justifyContent="space-between" direction="row">
              <Box>Pricing Setting Lock Date</Box>
              <Box fontWeight="bold">
                {isLoading || !account?.pricingSettingLockDate ? (
                  '--'
                ) : (
                  <Box direction="row" alignItems="center" gap="xs">
                    <Icon name="padlock" />
                    <DateTimeDisplay
                      value={account?.pricingSettingLockDate?.toString()}
                      showDuration={false}
                      dateFormat="DD"
                      inline
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box fontSize="xs" color="grey-400">
            * Pricing settings are locked upon the first time a contract is sent on an account.
          </Box>
        </Box>
      </Card>
    </>
  );
};
